import React from "react"
import { isMobileOnly } from "react-device-detect"
import clsx from "clsx"
import { useTranslation } from "react-i18next"

import * as BikeApi from "../../features/bike/api/BikeApi"
import { useAlert } from "../../hooks/useAlert"

import * as BikeFilters from "../../utils/bikeFilters"
import * as BikeMappers from "./Partials/bikeMappers"
import { schema } from "./Partials/schema"
import ProtectedLayout from "../../layouts/Protected"
import FormElement from "../../components/Form/FormElement"
import { Table } from "../../components/Elements/Table"
import { REFUSED_IMPORT_STATUS, VALIDATED_IMPORT_STATUS } from "../../utils/bikeValidation"
import { exportOutputErrorBikeCollectionFile } from "./Partials/exportXlsx"
import { isNotEmpty } from "../../utils/validation"
import { BikeImport, BikeImportError, BikeImportInfo, ErrorResponse } from "../../models"
import { FormError } from "../../models/form"
import { BikeIcon } from "components/Elements/Icons"
import { DEFAULT_SIZE } from "utils/icons"

export type AddBikeErrorProps = {
	bike: BikeImport
	errorTextArr: string
}

export type DefaultRowFromTemplateProps = {
	battery_serial_number: string
	frame_serial_number: string
	color: string
	bike_id: string
	private_owner_mail: string
	company_owner_mail: string
	brand: string
	model: string
	engine_serial_number: string
	last_name: string
	bike_oconsumer_id: string
	first_name: string
	social_reason: string
	private_owner_phone: string
	company_owner_phone: string
	gear_type: string
	is_electric: string
}

export const BikeCollectionImport = () => {
	const [bikeCollectionFromInputFile, setBikeCollectionFromInputFile] = React.useState<
		BikeImport[]
	>([])
	const [bikeCollectionImported, setBikeCollectionImported] = React.useState<BikeImport[]>([])
	const [bikeCollectionErrors, setBikeCollectionErrors] = React.useState<BikeImportError[]>([])
	const [bikeCollectionDisplayedInTable, setBikeCollectionDisplayedInTable] = React.useState<
		BikeImportInfo[]
	>([])

	const [isResultStepLoading, setIsResultStepLoading] = React.useState<boolean>(false)
	const [isImportFinished, setIsImportFinished] = React.useState<boolean>(false)
	const [showLocalErrors, setShowLocalErrors] = React.useState<boolean>(false)

	const [errors, setErrors] = React.useState<FormError[]>([])
	const { t, i18n } = useTranslation()
	const { alert } = useAlert()

	const FOOTER_TEXT = React.useMemo(
		() => t("footer_bike_collection_import", { ns: "bikeCollectionImport" }),
		[t]
	)

	// The given xlsx template comes with these empty rows:
	// (NB: for xlsx, "select_brand" label is a value)
	// (this defaultRow is then used to identify empty rows)
	const defaultRowFromTemplate: DefaultRowFromTemplateProps = React.useMemo(
		() => ({
			battery_serial_number: "",
			frame_serial_number: "",
			color: "",
			bike_id: "",
			private_owner_mail: "",
			company_owner_mail: "",
			brand: t("select_brand", { ns: "bikeCollectionImport" }),
			model: "",
			engine_serial_number: "",
			last_name: "",
			bike_oconsumer_id: "",
			first_name: "",
			social_reason: "",
			private_owner_phone: "",
			company_owner_phone: "",
			gear_type: t("select_type", { ns: "bikeCollectionImport" }),
			is_electric: t("global.no"),
		}),
		[t]
	)

	// Bike collection output onImportFinished
	// ---------------------------
	const createBikeCollectionResultsTable = () => {
		setBikeCollectionDisplayedInTable([
			...(bikeCollectionImported ?? []),
			...(bikeCollectionErrors ?? []),
		])
	}

	const exportErrorsOutputFile = async () => {
		try {
			await exportOutputErrorBikeCollectionFile(
				bikeCollectionErrors,
				bikeCollectionFromInputFile
			)
		} catch (error) {
			// console.log(error)
			alert("error", t("error_output_errors_file", { ns: "bikeCollectionImport" }))
		}
	}

	// Warning if the user load an another file, isImportFinished is not updated
	// Need to add bikeCollectionFromInputFile as dependancy
	React.useEffect(() => {
		if (isImportFinished) {
			createBikeCollectionResultsTable()
			if (isNotEmpty(bikeCollectionErrors)) exportErrorsOutputFile()
			if (!isNotEmpty(bikeCollectionErrors))
				alert("success", t("file_importation_success", { ns: "form" }))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isImportFinished, bikeCollectionFromInputFile])

	// Columns definition
	const columns = React.useMemo(
		() => [
			{
				Header: t("column_result", { ns: "bikeCollectionImport" }),
				accessor: "import_status",
				Cell: ({ row }) => {
					const isError = !!row.original.import_result.status
					const importResultText = row.original.import_result.message
					return <span className={isError ? "text-danger" : ""}>{importResultText}</span>
				},
				disableGlobalFilter: true,
			},
			{
				Header: t("column_bike_id", { ns: "bikeCollectionImport" }),
				accessor: "bike_id",
			},
			{
				Header: t("column_bike_brand", { ns: "bikeCollectionImport" }),
				accessor: "bike_description.brand",
				disableGlobalFilter: true,
			},
			{
				Header: t("column_bike_model", { ns: "bikeCollectionImport" }),
				accessor: "bike_description.model",
				disableGlobalFilter: true,
			},
			{
				Header: t("column_bike_type", { ns: "bikeCollectionImport" }),
				accessor: "bike_description.gear_type",
				disableGlobalFilter: true,
			},
			{
				Header: t("column_bike_is_electric", { ns: "bikeCollectionImport" }),
				accessor: "bike_description.is_electric",
				Cell: ({ value }) => {
					const isElectric = value ? t("global.yes") : t("global.no")
					return <span>{isElectric}</span>
				},
				disableGlobalFilter: true,
			},
			{
				Header: t("column_bike_color", { ns: "bikeCollectionImport" }),
				accessor: "bike_description.color",
				disableGlobalFilter: true,
			},
			// eslint-disable-next-line react-hooks/exhaustive-deps
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[i18n.language, t]
	)

	const addBikeError = ({ bike, errorTextArr }: AddBikeErrorProps) => {
		const bikeError: BikeImportInfo = {
			...bike,
			import_result: {
				status: REFUSED_IMPORT_STATUS,
				message: errorTextArr,
			},
		}
		setBikeCollectionErrors((prev) => [...prev, bikeError])
	}

	// Clean bike datas excel file of useless columns / rows
	// Removing header and footer is mandatory
	// ---------------------
	const removeUselessDataFromSheet = (dataFromFile: DefaultRowFromTemplateProps[]) => {
		let bikeCollectionRows: DefaultRowFromTemplateProps[] = []
		if (dataFromFile.length >= 50) {
			// get the first 50 rows (other rows as the footer will be removed)
			bikeCollectionRows = dataFromFile.slice(0, 50)
		} else {
			// else user have deleted some rows, we must find the footer.
			const endIndex = dataFromFile.findIndex((row) => {
				return row?.bike_id === FOOTER_TEXT
			})
			// get the first x rows
			bikeCollectionRows = dataFromFile.slice(0, endIndex)
		}

		const getStringRowValues = (row) => Object.entries(row).sort().toString()
		const isEmptyRow = (row) =>
			getStringRowValues(row) === getStringRowValues(defaultRowFromTemplate)

		// remove all non valued rows
		const cleanData = bikeCollectionRows.filter((row) => !isEmptyRow(row))

		return cleanData
	}

	// Reset function
	// ---------------------
	const clearBikeArrays = () => {
		setBikeCollectionFromInputFile([])
		setBikeCollectionImported([])
		setBikeCollectionErrors([])
		setBikeCollectionDisplayedInTable([])
	}

	// Create bikes (API calls)
	// ---------------------
	const createBikesFromCollection = async (bikeCollection) => {
		const importedBikeCollection: BikeImport[] = []
		try {
			for (const bike of bikeCollection) {
				// remove technical id to prevent AJV additionalProperties error.
				const bikeToImport = Object.assign({}, bike)
				delete bikeToImport.id
				// call API
				const { getError: getErrorFromPOSTCall } = await BikeApi.createBike(bikeToImport)
				if (getErrorFromPOSTCall()) {
					const { code: createBikeErrorCode, message: createBikeErrorMsg } =
						getErrorFromPOSTCall() as ErrorResponse
					if (createBikeErrorCode !== "BikeNotFoundException") {
						addBikeError({ bike, errorTextArr: createBikeErrorMsg })
						continue
					}

					// If a bike is already registered, the API returns a BikeNotFoundException when we try to create it.
					// We have to fetch the bike to know that.
					const { getError: getErrorFromGETcall } = await BikeApi.getBike(
						bike.bike_id.trim(),
						"byBikeId"
					)
					if (getErrorFromGETcall()) {
						const { message: consultBikeErrorMsg } =
							getErrorFromGETcall() as ErrorResponse
						addBikeError({ bike, errorTextArr: consultBikeErrorMsg })
					} else {
						addBikeError({
							bike,
							errorTextArr: t("error_bike_already_registered", {
								ns: "bikeCollectionImport",
							}),
						})
					}
				}

				if (!getErrorFromPOSTCall()) {
					importedBikeCollection.push({
						...bike,
						import_result: {
							status: VALIDATED_IMPORT_STATUS,
							message: t("import_status_success", { ns: "bikeCollectionImport" }),
						},
					})
				}
			}

			return importedBikeCollection
		} catch (error) {
			console.error(error)
			alert("error", t("error_create_bike_api", { ns: "bikeCollectionImport" }))
		}
	}

	// Bike import process
	// clean + validation + api call
	// ---------------------
	const importBikeCollection = async (dataFromFile: DefaultRowFromTemplateProps[]) => {
		setIsResultStepLoading(true)
		setIsImportFinished(false)

		// 1 - file is cleaned : remove useless rows / cells
		const bikeCollectionFromFile = removeUselessDataFromSheet(dataFromFile)

		// 2 - format data to be ready as body payload.
		const formattedBikeCollection: BikeImport[] =
			BikeMappers.mapRowsFileToBikeCollectionObj(bikeCollectionFromFile)
		setBikeCollectionFromInputFile(formattedBikeCollection)

		// 3 - manual bike collection validation
		const formattedAndManuallyFilteredBikeCollection =
			BikeFilters.filterManuallyCheckedBikeCollection({
				bikes: formattedBikeCollection,
				addBikeError,
			})

		// 4 - delegate bike validation (eg : AJV extern library - check with regex pattern, check length ...)
		const formattedAndFilteredBikeCollection = BikeFilters.delegateBusinessLogicFilter(
			formattedAndManuallyFilteredBikeCollection as BikeImport[],
			schema,
			addBikeError
		)

		// 5 - call API to insert bike collection in the database and return only imported bikes (response 200)
		const importedBikeCollection = await createBikesFromCollection(
			formattedAndFilteredBikeCollection
		)

		setBikeCollectionImported(importedBikeCollection as BikeImport[])
		setIsImportFinished(true)
		setIsResultStepLoading(false)
	}

	// Local handle functions
	// ---------------------
	const handleInputChange = React.useCallback(
		(dataFromFile: DefaultRowFromTemplateProps[]) => {
			const formReady = !(errors.length > 0)

			if (!formReady) {
				setShowLocalErrors(true)
				alert("error", t("error_form", { ns: "bikeCollectionImport" }))
				return
			}

			// if the user load an another file we clear bike arrays.
			if (bikeCollectionDisplayedInTable.length > 0) clearBikeArrays()

			setShowLocalErrors(false)
			importBikeCollection(dataFromFile)

			if (dataFromFile.length <= 0) {
				setBikeCollectionImported([])
				setBikeCollectionErrors([])
				setIsImportFinished(true)
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[bikeCollectionDisplayedInTable]
	)

	const handleValidation = React.useCallback((name: string, errs: FormError[]) => {
		setErrors((s) => {
			const cleanErrors = [...s.filter((e) => e.origin !== name)]
			return [...cleanErrors, ...errs]
		})
	}, [])

	return (
		<ProtectedLayout className="py-5">
			<div className="l-container">
				{/* Page title */}
				<div
					className={clsx("c-section-title mb-7", {
						"px-8": !isMobileOnly,
						"px-6": isMobileOnly,
					})}
				>
					<div className={clsx("c-section-title__content", { "flex-row": isMobileOnly })}>
						<BikeIcon size={DEFAULT_SIZE} />
						<h1 className="c-section-title__title c-h2 text-primary ml-4">
							{t("menu.bike_collection_import")}
						</h1>
					</div>
				</div>

				<div
					className={clsx("flex flex-col", {
						"px-8": !isMobileOnly,
						"px-6": isMobileOnly,
					})}
				>
					{/* Step 1 - Excel file template download */}
					<div className={clsx("c-card p-7 my-5", { "w-full ": !isMobileOnly })}>
						<h3>{t("title_step1", { ns: "bikeCollectionImport" }).toUpperCase()}</h3>
						<p className="py-4">
							{t("description_step1", { ns: "bikeCollectionImport" })}
						</p>
						<button className="c-btn c-btn--primary px-7">
							<a
								href={`${process.env.PUBLIC_URL}/${t("import_bike_file_name", {
									ns: "bikeCollectionImport",
								})}`}
								target="_blank"
								rel="noopener noreferrer"
							>
								{t("btn_label_step1", { ns: "bikeCollectionImport" })}
							</a>
						</button>
					</div>

					{/* Step 2 - Excel file import */}
					<div className={clsx("c-card p-7 my-5", { "w-full ": !isMobileOnly })}>
						<h3>{t("title_step2", { ns: "bikeCollectionImport" }).toUpperCase()}</h3>
						<p className="py-4">
							{t("description_step2", { ns: "bikeCollectionImport" })}
						</p>
						<div className="inline-block">
							<FormElement
								type="xlsx"
								id="upload"
								name="bikesFile"
								classNameInput="px-7 mr-5"
								showErrors={showLocalErrors}
								placeholder={t("btn_label_step2", { ns: "bikeCollectionImport" })}
								onChange={handleInputChange}
								onValidate={handleValidation}
							/>
						</div>
					</div>

					{/* Step 3 - Importation results */}
					<div className={clsx("c-card p-7 my-5", { "w-full ": !isMobileOnly })}>
						<h3>{t("title_step3", { ns: "bikeCollectionImport" }).toUpperCase()}</h3>

						{isImportFinished && (
							<p className="py-2">
								{t("import-succes", { ns: "bikeCollectionImport" })} :{" "}
								<span className="text-successColor">
									{bikeCollectionImported.length}
								</span>{" "}
								- {t("import-error", { ns: "bikeCollectionImport" })} :{" "}
								<span className="text-danger">{bikeCollectionErrors.length}</span>
							</p>
						)}

						{!isResultStepLoading && (
							<>
								<p className="py-4">
									{t("description_step3", { ns: "bikeCollectionImport" })}
								</p>
								{isImportFinished && !!bikeCollectionDisplayedInTable.length && (
									<Table
										columns={columns}
										data={bikeCollectionDisplayedInTable}
										isPaginable={true}
										isSortable={false}
										searchPlaceholder={t("search_placeholder", {
											ns: "bikeCollectionImport",
										})}
									/>
								)}
							</>
						)}
						{isResultStepLoading && (
							<>
								<p className="py-4">
									{t("description_loading_step3", { ns: "bikeCollectionImport" })}
								</p>
								<div className="flex justify-center m-6">
									<div className="c-spinner"></div>
								</div>
							</>
						)}
					</div>
				</div>
			</div>
		</ProtectedLayout>
	)
}
